<template>
  <EmptySubscriptionPage
    v-if="!hasFeatureFlag('module-register')"
    :fullHeight="true"
    :text="{
      title: $t(`upgrade_subscription_page.register.${orderKey('plural')}.title`),
      description: $t(
        `upgrade_subscription_page.register.${orderKey('plural')}.description`
      ),
      btnPrimary: $t('upgrade_subscription_page.upgrade_btn')
    }"
    :moreLink="{
      text: $t(`upgrade_subscription_page.register.${orderKey('plural')}.learn_more`),
      url: helpCenterArticleUrl(
        $t(`upgrade_subscription_page.register.${orderKey('plural')}.learn_more_url`),
      )
    }"
    imageName="invoices"
  />

  <Flex
    direction="column"
    v-else
  >
    <router-view />
    <PageHeader>
      <template #left>
        <div
          v-if="hasFeatureFlag('beta-cashup')"
          :class="$style.switch"
        >
          <BaseSwitch
            v-model="view"
            :options="[
              { label: t('global.sales'), value: 'sales' },
              { label: t('global.items.receipt', 2), value: 'receipts' },
              { label: t('global.items.invoice', 2), value: 'invoices' },
            ]"
            v-test="'register-sales-view-switch'"
          />
        </div>
        <BaseDropdown
          v-if="view === 'sales'"
          v-model="variables.filterScope"
          :label="$t('global.state')"
          :options="
            stateOptions
              .filter((option) => !option.hide)
              .map(({ hide, ...option }) => option)
          "
          :visibleItemCount="7"
        />
        <BaseDropdown
          v-if="view === 'sales'"
          v-model="variables.employeeId"
          :label="$t('global.items.employee', 1)"
          :options="[
            {
              value: null,
              label: filters.capitalize($t('global.all'))
            },
            ...employees.map((employee) => ({
              value: employee.id,
              label: employee.name
            }))
          ]"
          resources
        />
        <SalesSearch
          v-if="view === 'sales'"
          v-model:search="variables.search"
          v-model:customerId="variables.customerId"
          :placeholder="searchPlaceholder"
        />
        <BaseSearch
          v-else
          v-model="activeSearch"
          :placeholder="searchPlaceholder"
          debounce
        />
      </template>
    </PageHeader>
    <BaseCard v-if="rows.length">
      <BaseTable
        :headers="headers"
        :rows="rows"
        :sortOrder="
          view === 'sales'
            ? { sortBy: variables.sortBy, direction: variables.direction }
            : undefined
        "
        @sort="
          ($event) => {
            variables.sortBy = $event.sortBy;
            variables.direction = $event.direction;
          }
        "
        v-test="`register-${view}`"
        @action="($event) => onDocumentAction(view, $event)"
      />
    </BaseCard>
    <EmptyPageContent
      v-show="!activeLoading && !rows.length"
      imageName="invoices"
      :text="showEmptyPageContent
        ? {
            title: $t(`upgrade_subscription_page.register.${orderKey('plural')}.title`),
            description: $t(
              `upgrade_subscription_page.register.${orderKey('plural')}.description`
            ),
            btnPrimary: $t(`global.actions.create_${orderKey()}`)
          }
        : null
      "
      :moreLink="
        showEmptyPageContent
          ? {
              text: $t(`${orderKey('plural')}.empty_state.more_text`),
              url: helpCenterCollectionUrl($t(`${orderKey('plural')}.empty_state.more_url`))
            }
          : null
      "
      @click="$router.push({ name: 'register-builder' })"
    />
    <LoadMore
      v-show="!activeAllDataFetched"
      @shown="activeFetchMore"
    />
    <EmailReceipt
      v-if="showEmailReceiptModal"
      :email="selectedDocumentEmail"
      :documentId="selectedDocumentId"
      :orderId="selectedDocumentOrderId"
      @submit="showEmailReceiptModal = false"
      @close="showEmailReceiptModal = false"
    />
  </Flex>
</template>
<script
  setup
  lang="ts"
>
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import config from '@/config';
import filters from '@/filters';
import { orderKey } from '@/helpers/compliant-register';
import {
  helpCenterArticleUrl,
  helpCenterCollectionUrl,
} from '@/helpers/help-center';
import EmailReceipt from '@/modals/EmailReceipt.vue';
import EmptySubscriptionPage from '@/modules/_shared/subscription/EmptySubscriptionPage.vue';
import PageHeader from '@/modules/PageHeader.vue';
import { useCompanyStore } from '@/stores/company';
import { useNativeAppStore } from '@/stores/native-app';
import { useResourcesStore } from '@/stores/resources';
import { useUserStore } from '@/stores/user';
import { REGISTER_DOCUMENTS_QUERY } from './graphql';
import SalesSearch from './SalesSearch.vue';

const { isNativeApp } = useNativeAppStore();
const router = useRouter();

interface Option {
  value: string | null;
  label: string;
  hide?: boolean;
}

defineOptions({
  name: 'Sales',
});

const { t } = useI18n();
const { hasFeatureFlag } = useUserStore();
const { multiLocation } = storeToRefs(useCompanyStore());
const { employees } = storeToRefs(useResourcesStore());

const view = ref<'sales' | 'receipts' | 'invoices'>('sales');

const { orders, loading, variables, allDataFetched, fetchMore }: any =
  usePagination({
    fieldName: 'orders',
    query: gql`
      query getOrders(
        $filterScope: FilterScope
        $search: String
        $customerId: ID
        $locationId: ID
        $dataScope: DataScope
        $employeeId: ID
        $direction: SortDirectionEnum
        $pagination: PaginationAttributes
        $sortBy: String
      ) {
        orders(
          filterScope: $filterScope
          search: $search
          customerId: $customerId
          locationId: $locationId
          dataScope: $dataScope
          employeeId: $employeeId
          direction: $direction
          pagination: $pagination
          sortBy: $sortBy
        ) {
          id
          number
          invoicedAt
          location {
            id
            name
          }
          employeeNames
          customer {
            fullName
          }
          total
          state
        }
      }
    `,
    variables: {
      filterScope: null,
      search: '',
      customerId: null,
      employeeId: null,
      direction: 'DESC',
      sortBy: 'created_on',
    },
    options: {
      multiLocation: true,
      enabled: computed(() => view.value === 'sales'),
    },
  });

const {
  registerDocuments: receiptDocuments,
  loading: receiptsLoading,
  variables: receiptsVariables,
  allDataFetched: receiptsAllDataFetched,
  fetchMore: fetchMoreReceipts,
}: any = usePagination({
  fieldName: 'registerDocuments',
  query: REGISTER_DOCUMENTS_QUERY,
  variables: {
    view: 'RECEIPTS',
    search: '',
  },
  options: {
    multiLocation: true,
    enabled: computed(() => view.value === 'receipts'),
  },
});

const {
  registerDocuments: invoiceDocuments,
  loading: invoicesLoading,
  variables: invoicesVariables,
  allDataFetched: invoicesAllDataFetched,
  fetchMore: fetchMoreInvoices,
}: any = usePagination({
  fieldName: 'registerDocuments',
  query: REGISTER_DOCUMENTS_QUERY,
  variables: {
    view: 'INVOICES',
    search: '',
  },
  options: {
    multiLocation: true,
    enabled: computed(() => view.value === 'invoices'),
  },
});

provide(
  'paginationAllDataFetched',
  computed(() => {
    if (view.value === 'receipts') return receiptsAllDataFetched.value;
    if (view.value === 'invoices') return invoicesAllDataFetched.value;
    return allDataFetched.value;
  }),
);

const activeLoading = computed(() => {
  if (view.value === 'receipts') return receiptsLoading.value;
  if (view.value === 'invoices') return invoicesLoading.value;
  return loading.value;
});

const activeAllDataFetched = computed(() => {
  if (view.value === 'receipts') return receiptsAllDataFetched.value;
  if (view.value === 'invoices') return invoicesAllDataFetched.value;
  return allDataFetched.value;
});

const activeFetchMore = () => {
  if (view.value === 'receipts') return fetchMoreReceipts();
  if (view.value === 'invoices') return fetchMoreInvoices();
  return fetchMore();
};

const activeSearch = computed<string>({
  get: () => {
    if (view.value === 'receipts') return receiptsVariables.search;
    if (view.value === 'invoices') return invoicesVariables.search;
    return variables.search;
  },
  set: (value: string) => {
    if (view.value === 'receipts') {
      receiptsVariables.search = value;
    } else if (view.value === 'invoices') {
      invoicesVariables.search = value;
    } else {
      variables.search = value;
    }
  },
});

const searchPlaceholder = computed(() => {
  const item =
    view.value === 'receipts'
      ? t('global.items.receipt', 2)
      : view.value === 'invoices'
        ? t('global.items.invoice', 2)
        : t('global.items.sale', 3);

  return t('global.actions.search_item', { item });
});

const documentActions = (kind: 'receipts' | 'invoices') => [
  {
    name: 'view-pdf',
    icon: 'pdf',
    label: t('global.actions.view_receipt_pdf'),
  },
  {
    name: 'print',
    icon: 'printer',
    label:
      kind === 'receipts'
        ? t('global.actions.print_receipt')
        : t('global.actions.print_item', {
            item: t('global.items.invoice', 1),
          }),
  },
  {
    name: 'email',
    icon: 'paper-plane',
    label: t('global.actions.email_item', {
      item: t(`global.items.${kind === 'receipts' ? 'receipt' : 'invoice'}`, 1),
    }),
  },
];

const showEmailReceiptModal = ref(false);
const selectedDocumentId = ref<string | null>(null);
const selectedDocumentEmail = ref<string | undefined>(undefined);
const selectedDocumentOrderId = ref<number | undefined>(undefined);

const onDocumentAction = (
  kind: 'sales' | 'receipts' | 'invoices',
  { id, option }: { id: string | number; option: string },
) => {
  if (kind === 'sales') return;

  const doc = (
    kind === 'receipts' ? receiptDocuments.value : invoiceDocuments.value
  ).find((d: any) => d.id === id);
  if (!doc) return;

  if (option === 'view-pdf' || option === 'print') {
    const action = option === 'view-pdf' ? 'view' : 'print';
    const url = `${config.backendUrl}/register/documents/${doc.id}/${action}`;
    if (isNativeApp) {
      // Native app: doing window.print() from a window.open() page prints the
      // opener instead of the document. Instead we need to navigate to the document page and print from there.
      // back=1 tells the document page to print the document and then go back to the previous page.
      window.location.href = option === 'print' ? `${url}?back=1` : url;
    } else {
      window.open(url, '_blank');
    }
  } else if (option === 'email') {
    if (kind === 'receipts') {
      selectedDocumentId.value = doc.id;
      selectedDocumentEmail.value = doc.order.customer?.email;
      selectedDocumentOrderId.value = Number(doc.order.id);
      showEmailReceiptModal.value = true;
    } else {
      router.push({
        name: 'document-email',
        params: { orderId: doc.order.id, documentId: doc.id },
      });
    }
  }
};

const headers = computed(() => {
  const location = {
    value: t('global.items.location', 1),
    hide: !multiLocation.value,
  };
  const employeesHeader = {
    value: t('global.items.employee', 2),
    hide: !showEmployees.value,
  };
  const customer = t('global.items.customer', 1);
  const status = t('global.status');

  if (view.value === 'receipts') {
    return [
      t('global.number'),
      t('global.items.sale', 1),
      t('global.date'),
      location,
      customer,
      {
        value: t('global.total'),
        alignRight: true,
      },
    ];
  }

  if (view.value === 'invoices') {
    return [
      t('global.number'),
      t('global.items.sale', 1),
      t('register.invoice.issue_date'),
      t('register.invoice.due_date'),
      location,
      customer,
      {
        value: t('global.total'),
        alignRight: true,
      },
    ];
  }

  return [
    { value: t('global.number'), sort: 'number' },
    { value: t('global.date'), sort: 'created_on' },
    location,
    employeesHeader,
    customer,
    {
      value: t('global.total'),
      alignRight: true,
      sort: 'total',
    },
    status,
  ];
});

const rows = computed(() => {
  if (view.value === 'receipts') {
    return receiptDocuments.value.map((doc: any) => ({
      id: doc.id,
      routerLink: { name: 'sale', params: { id: doc.order.id } },
      cells: [
        doc.number,
        doc.order.number,
        filters.dateTime(doc.issuedAt),
        { value: doc.order.location?.name, hide: !multiLocation.value },
        doc.order.customer?.fullName,
        {
          value: filters.currency(doc.totalAmountInclTaxExempt),
          alignRight: true,
        },
      ],
      actions: documentActions('receipts'),
    }));
  }

  if (view.value === 'invoices') {
    return invoiceDocuments.value.map((doc: any) => ({
      id: doc.id,
      routerLink: { name: 'sale', params: { id: doc.order.id } },
      cells: [
        doc.number,
        doc.order.number,
        filters.dateTime(doc.issuedAt),
        filters.date(doc.dueDate),
        { value: doc.order.location?.name, hide: !multiLocation.value },
        doc.order.customer?.fullName,
        {
          value: filters.currency(doc.totalAmountInclTaxExempt),
          alignRight: true,
        },
      ],
      actions: documentActions('invoices'),
    }));
  }

  return orders.value.map((o: any) => ({
    id: o.id,
    routerLink: { name: 'sale', params: { id: o.id } },
    cells: [
      { value: o.number, component: 'sale-number' },
      filters.dateTime(o.invoicedAt),
      { value: o.location.name, hide: !multiLocation.value },
      { value: o.employeeNames, hide: !showEmployees.value },
      o.customer?.fullName,
      { value: filters.currency(o.total), alignRight: true },
      { label: o.state?.toLowerCase() },
    ],
  }));
});

const showEmptyPageContent = computed(() => {
  if (view.value === 'receipts' || view.value === 'invoices') {
    return false;
  }
  return (
    !variables.filterScope &&
    !variables.search &&
    !variables.customerId &&
    !variables.employeeId &&
    !orders.value.length
  );
});

const stateOptions = computed<Option[]>(() => [
  {
    value: null,
    label: t('global.all'),
  },
  {
    value: 'DRAFT',
    label: t('global.draft'),
  },
  {
    value: 'OUTSTANDING',
    label: t('global.outstanding'),
  },
  {
    value: 'PAID',
    label: t('global.paid'),
  },
  {
    value: 'ONLINE',
    label: t('global.online'),
  },
  {
    value: 'VOIDED',
    label: t('global.cancelled'),
    hide: !hasFeatureFlag('compliant-register'),
  },
  {
    value: 'REFUNDED',
    label: t('global.refunded'),
    hide: !hasFeatureFlag('compliant-register'),
  },
]);

const showEmployees = computed(() => employees.value.length > 1);
</script>

<style
  lang="scss"
  module
>
.switch {
  // added this else the page jumps when selecting a different view without filters
  margin-top: $spacing * 1.5;
}
</style>
